import React, { useState, useEffect } from "react";
import "./AddEditManualModal.css";
import { useLanguage } from "../../../../LanguageProvider";
import AddModalInputField from "../../../../components/AddModalInputField/AddModalInputField";
import { TbStack2 } from "react-icons/tb";
import DatePicker from "../../../../components/DatePicker/DatePicker";
import TimePicker from "../../../../components/TimePicker/TimePicker";
import { useToaster } from "../../../../ToasterProvider";
import Dropdown from "../../../../components/Dropdown/Dropdown";

const AddEditManualModal = ({
  isOpen,
  type,
  onClose,
  onSubmit,
  data,
  onEdit,
  groups,
  selectedGroupAtKronos,
  subProjects
}) => {
  const { getTranslation } = useLanguage();
  const { toastWarning } = useToaster();
  const [title, setTitle] = useState("");
  const [startDate, setStartDate] = useState("");
  const [startTime, setStartTime] = useState("");
  const [endDate, setEndDate] = useState("");
  const [endTime, setEndTime] = useState("");
  const [selectedGroup, setSelectedGroup] = useState("empty");
  const [selectedSubProject, setSelectedSubProject] = useState("empty");

  const handleSubmit = () => {
    if (!title.trim() || !startDate || !startTime || !endDate || !endTime) {
      toastWarning(getTranslation("pleaseFillAllFields"));
      return;
    }

    const formatDateTime = (date, time) => {
      const d = new Date(date);
      const formattedDate = `${d.getFullYear()}-${String(
        d.getMonth() + 1
      ).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;

      const [timePart, meridian] = time.split(" ");
      let [hours, minutes] = timePart.split(":");

      if (meridian === "PM" && hours !== "12") hours = parseInt(hours, 10) + 12;
      if (meridian === "AM" && hours === "12") hours = "00";

      return `${formattedDate} ${String(hours).padStart(2, "0")}:${minutes}:00`;
    };

    const startDateTime = formatDateTime(startDate, startTime);
    const endDateTime = formatDateTime(endDate, endTime);

    const isSameDay = (date1, date2) => {
      return (
        date1.getFullYear() === date2.getFullYear() &&
        date1.getMonth() === date2.getMonth() &&
        date1.getDate() === date2.getDate()
      );
    };

    if (isSameDay(startDate, endDate) && endDateTime <= startDateTime) {
      toastWarning(getTranslation("endTimeCannotBeBeforeStartTime"));
      return;
    }

    const submissionData = {
      title,
      startDateTime,
      endDateTime,
      groupId: selectedGroup !== "empty" ? selectedGroup : null,
      subProjectId: selectedSubProject !== "empty" ? selectedSubProject : null,
    };

    if (type === "edit") {
      onEdit(submissionData);
    } else {
      onSubmit(submissionData);
    }

    setTitle("");
    setStartDate("");
    setEndDate("");
    setStartTime("");
    setEndTime("");
    setSelectedGroup("empty");
    setSelectedSubProject("empty");
    onClose();
  };

  useEffect(() => {
    if (type === "edit" && data) {
      const startDateTime = new Date(parseInt(data.startDateTime, 10) * 1000);
      const endDateTime = new Date(parseInt(data.endDateTime, 10) * 1000);

      setTitle(data.title);
      setStartDate(
        `${startDateTime.getFullYear()}-${String(
          startDateTime.getMonth() + 1
        ).padStart(2, "0")}-${String(startDateTime.getDate()).padStart(2, "0")}`
      );
      setStartTime(
        startDateTime.toLocaleTimeString([], {
          hour: "2-digit",
          minute: "2-digit",
          hour12: false,
        })
      );
      setEndDate(
        `${endDateTime.getFullYear()}-${String(
          endDateTime.getMonth() + 1
        ).padStart(2, "0")}-${String(endDateTime.getDate()).padStart(2, "0")}`
      );
      setEndTime(
        endDateTime.toLocaleTimeString([], {
          hour: "2-digit",
          minute: "2-digit",
          hour12: false,
        })
      );
      // Eğer edit modunda ve data.groupId varsa onu, yoksa "empty" olarak ayarla
      setSelectedGroup(data.groupId ? data.groupId : "empty");
      setSelectedSubProject(data.subProjectId ? data.subProjectId : "empty");
    } else {
      // Add modunda, eğer Kronos'ta bir grup seçili ise onu kullan, yoksa "empty"
      setTitle("");
      setStartDate("");
      setStartTime("");
      setEndDate("");
      setEndTime("");
      setSelectedGroup(selectedGroupAtKronos ? selectedGroupAtKronos : "empty");
      setSelectedSubProject("empty");
    }
  }, [data, type, selectedGroupAtKronos]);

  const handleClose = () => {
    setTitle("");
    setStartDate("");
    setStartTime("");
    setEndDate("");
    setEndTime("");
    setSelectedGroup("empty");
    setSelectedSubProject("empty");
    onClose();
  };

  if (!isOpen) return null;

  return (
    <div className="add-manual-modal-overlay">
      <div
        className="add-manual-modal"
        style={{ height: (groups || subProjects) && (groups?.length > 1 || subProjects?.length > 0) ? 480 : 400 }}
      >
        <span className="add-manual-modal-title">
          {type === "edit"
            ? getTranslation("edit")
            : getTranslation("addManual")}
        </span>

        <span className="add-manual-modal-subtitle">
          {getTranslation("title")}
        </span>
        <AddModalInputField
          icon={<TbStack2 size={22} style={{ width: "25px" }} />}
          placeholder={getTranslation("whatAreYouWorkingOn")}
          value={title}
          disabled={data?.taskId?.length > 1}
          onChange={(e) => setTitle(e.target.value)}
          type="text"
        />

        {/* Grup seçenekleri varsa dropdown göster */}

        <div style={{ flexDirection: 'row', display: 'flex', gap: '10px' }}>

          {groups && groups?.length > 1 && (
            <div style={{ flexDirection: 'column', display: 'flex', width: '50%', gap: '10px' }}>
              <span className="add-manual-modal-subtitle">
                {getTranslation("groups")}
              </span>
              <Dropdown
                style={{ width: '100%' }}
                options={groups}
                onChange={(e) => setSelectedGroup(e.target.value)}
                value={selectedGroup}
              />
            </div>
          )}

          {subProjects && subProjects.length > 0 && (
            <div style={{ flexDirection: 'column', display: 'flex', width: '50%', gap: '10px' }}>
              <span className="add-manual-modal-subtitle">
                {getTranslation("selectProject")}
              </span>
              <Dropdown
                placeholder={getTranslation("selectProject")}
                style={{ width: '100%' }}
                options={[
                  { value: "empty", label: getTranslation("none") },
                  ...subProjects
                ]}
                onChange={(e) => setSelectedSubProject(e.target.value)}
                value={selectedSubProject}
              />
            </div>
          )}

        </div>
        <span className="add-manual-modal-subtitle">
          {getTranslation("startDate")}
        </span>
        <div style={{ display: "flex", gap: "10px" }}>
          <DatePicker
            style={{ width: "50%" }}
            buttonStyle={{ width: "100%" }}
            onDateSelect={(date) => setStartDate(date)}
            toggleLabel={getTranslation("startDate")}
            initialDate={startDate !== "" ? startDate : null}
          />
          <TimePicker
            style={{ width: "50%" }}
            buttonStyle={{ width: "100%" }}
            onTimeSelect={(time) => setStartTime(time)}
            toggleLabel={getTranslation("startTime")}
            initialTime={startTime !== "" ? startTime : null}
          />
        </div>

        <span className="add-manual-modal-subtitle">
          {getTranslation("endDate")}
        </span>
        <div style={{ display: "flex", gap: "10px" }}>
          <DatePicker
            style={{ width: "50%" }}
            buttonStyle={{ width: "100%" }}
            onDateSelect={(date) => setEndDate(date)}
            toggleLabel={getTranslation("endDate")}
            initialDate={endDate !== "" ? endDate : null}
            dropdownPosition="top"
            minDate={startDate}
            resetDate={startDate}
          />
          <TimePicker
            style={{ width: "50%" }}
            buttonStyle={{ width: "100%" }}
            onTimeSelect={(time) => setEndTime(time)}
            toggleLabel={getTranslation("endTime")}
            initialTime={endTime !== "" ? endTime : null}
            dropdownPosition="top"
          />
        </div>

        <div style={{ display: "flex", gap: "10px", marginTop: "20px" }}>
          <button
            className="add-manual-modal-cancel-button"
            onClick={handleClose}
          >
            {getTranslation("cancel")}
          </button>

          <button
            className="add-manual-modal-add-button"
            onClick={handleSubmit}
          >
            {type === "edit" ? getTranslation("save") : getTranslation("add")}
          </button>
        </div>
      </div>
    </div>
  );
};

export default AddEditManualModal;
